<script lang="ts">
// @ts-ignore - compat: Vue 2 + composition API
import { defineComponent, computed, ref } from 'vue';
import { CopyIcon, ThumbsUpIcon, ThumbsDownIcon } from 'vue-feather-icons';
import type { TMessage } from '@/models/CopilotChat';
import ResultDialog from './ResultDialog.vue';
import { useResultDialog } from './useResultDialog';
// Feedback dialog foi movido para o componente pai para evitar clipping

export default defineComponent({
  name: 'ChatMessage',
  components: { CopyIcon, ThumbsUpIcon, ThumbsDownIcon, ResultDialog },
  props: {
    message: { type: Object as () => TMessage, required: true },
    dark: { type: Boolean, default: false },
    reaction: { type: Boolean, default: null },
  },
  emits: ['copy', 'like', 'dislike', 'show-knowledge-base', 'open-feedback', 'scroll-bottom'],
  setup(
    props: { message: TMessage; dark: boolean; reaction: boolean | null },
    { emit }: { emit: (event: string, ...args: any[]) => void }
  ) {
    const isAssistant = computed(() => props.message.sender === 'assistant');
    const containerClass = computed(() =>
      isAssistant.value ? 'flex justify-start' : 'flex justify-end'
    );
    const userClass = computed(() =>
      [
        props.dark
          ? 'bg-[rgba(126,103,240,0.5)] text-gray-900'
          : 'bg-[rgba(126,103,240,0.2)] text-gray-900',
        'rounded-2xl px-4 py-3 max-w-[100%] break-words',
      ].join(' ')
    );

    const { isDialogOpen, openDialog, closeDialog } = useResultDialog();
    const hasSource = computed(() => {
      if (!props.message.source || props.message.source.length === 0) return null;
      return props.message.source;
    });

    const likeAnim = ref(false);
    const dislikeAnim = ref(false);

    const handleLike = () => {
      likeAnim.value = true;
      setTimeout(() => { likeAnim.value = false; }, 500);
      emit('like', props.message);
    };

    const handleDislike = (e: MouseEvent) => {
      dislikeAnim.value = true;
      setTimeout(() => { dislikeAnim.value = false; }, 500);
      const target = (e.currentTarget || e.target) as HTMLElement;
      const rect = target?.getBoundingClientRect?.() as DOMRect | undefined;
      emit('open-feedback', { message: props.message, rect });
      emit('dislike', props.message);
    };

    const handleResultClick = () => {
      openDialog();
    };

    return {
      isAssistant,
      containerClass,
      userClass,
      handleLike,
      handleDislike,
      isDialogOpen,
      openDialog,
      closeDialog,
      handleResultClick,
      hasSource,
      likeAnim,
      dislikeAnim
    };
  },
});
</script>

<template>
  <div :class="containerClass">
    <div
      v-if="isAssistant"
      class="bg-white text-gray-900 rounded-2xl px-4 py-4 pb-11 shadow-sm max-w-[100%] relative"
    >
      <slot />
      <div class="mt-2 flex justify-start" v-if="hasSource">
        <span
          @click="$emit('show-knowledge-base', message.source)"
          class="text-xs text-gray-400 cursor-pointer hover:text-purple-600 select-none"
        >
          Fontes: Base de conhecimento
        </span>
      </div>
      <div class="flex justify-between items-end w-full absolute left-0 bottom-0 px-2 pb-1">
        <button
          @click="$emit('copy', message.content)"
          class="p-2 text-gray-400 hover:bg-purple-100 hover:text-purple-500 transition rounded-full"
          aria-label="Copiar mensagem"
        >
          <CopyIcon size="14" />
        </button>
        <div class="flex gap-1">
          <button
            @click="handleLike"
            class="p-2 text-gray-400 hover:bg-green-100 hover:text-green-500 transition rounded-full relative"
            aria-label="Curtir mensagem"
          >
            <ThumbsUpIcon
              size="14"
              :class="[
                'transition-all duration-300',
                reaction ? 'text-green-500 fill-current' : '',
                likeAnim ? 'animate-like-pop' : ''
              ]"
            />
          </button>
          <button
            @click="handleDislike"
            class="p-2 text-gray-400 hover:bg-red-100 hover:text-red-500 transition rounded-full relative"
            aria-label="Não curtir mensagem"
          >
            <ThumbsDownIcon
              size="14"
              :class="[
                'transition-all duration-300',
                !reaction && reaction !== null ? 'text-red-500 fill-current' : '',
                dislikeAnim ? 'animate-dislike-shake' : ''
              ]"
            />
          </button>
        </div>
      </div>
    </div>
    <div v-else :class="userClass">
      <slot />
    </div>
  </div>
</template>

<style scoped lang="scss">
@keyframes like {
  0% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.2);
  }
  100% {
    transform: scale(1);
  }
}

@keyframes explosion {
  0% {
    transform: scale(1);
    opacity: 0.8;
  }
  50% {
    transform: scale(1.5);
    opacity: 0;
  }
  100% {
    transform: scale(1.5);
    opacity: 0;
  }
}

.animate-like {
  animation: like 0.5s ease-in-out;
}

.animate-explosion {
  animation: explosion 0.5s ease-out;
  border-radius: 50%;
}

.animate-explosion:has(+ .text-green-500) {
  background: radial-gradient(circle, rgba(34, 197, 94, 0.2) 0%, rgba(34, 197, 94, 0) 70%);
}

.animate-explosion:has(+ .text-red-500) {
  background: radial-gradient(circle, rgba(239, 68, 68, 0.2) 0%, rgba(239, 68, 68, 0) 70%);
}

@keyframes like-pop {
  0% { transform: scale(1); }
  30% { transform: scale(1.3) rotate(-10deg); }
  60% { transform: scale(1.1) rotate(8deg); }
  100% { transform: scale(1) rotate(0deg); }
}
.animate-like-pop {
  animation: like-pop 0.5s cubic-bezier(0.4, 0.8, 0.2, 1);
}
@keyframes dislike-shake {
  0% { transform: rotate(0deg); }
  20% { transform: rotate(-15deg); }
  40% { transform: rotate(10deg); }
  60% { transform: rotate(-8deg); }
  80% { transform: rotate(6deg); }
  100% { transform: rotate(0deg); }
}
.animate-dislike-shake {
  animation: dislike-shake 0.5s cubic-bezier(0.4, 0.8, 0.2, 1);
}
</style>
